declare global {
  interface Window {
    dataLayer: any;
  }
}
export interface CartAnalyticsInfo {
  currency: string;
  value: number;
  items: Array<ItemAnalyticsInfo>;
}

export interface ItemAnalyticsInfo {
  item_id: string;
  item_name: string;
  item_brand: string;
  item_variant: string;
  price: number;
  quantity: number;
  time_in_cart?: number;
  lob?: string;
}

export interface OrderAnalyticsInfo {
  transaction_id: string;
  value: number;
  tax: string;
  shipping: string;
  currency: string;
  items: Array<ItemAnalyticsInfo>;
  payment_type?: string;
}

interface CartItemsDetails {
  product: {
    productId: string;
    title: string;
    productType: string;
  };
  priceOption: {
    variantId: string;
    unitPrice: number;
    quantity: number;
  };
  dateAdded?: string;
}

interface CartForAnalytics {
  lob: string;
  items: CartItemsDetails[];
}

function pushUnifiedViewCart(carts: CartForAnalytics[], lobDisplayNames: Record<string, string>) {
  const items: ItemAnalyticsInfo[] = carts.flatMap(({ lob, items }) =>
    items.map((item) => ({
      item_id: `${item.product.productId}`,
      item_name: `${item.product.title}`,
      item_brand: `${item.product.productType}`,
      item_variant: item.priceOption.variantId,
      lob: lobDisplayNames[lob] || lob,
      price: item.priceOption.unitPrice,
      quantity: item.priceOption.quantity,
      time_in_cart: getTimeInCart(item.dateAdded),
    }))
  );

  if (items.length > 0) {
    viewCartEvent({
      currency: 'USD',
      value: items.reduce((total, item) => total + item.price * item.quantity, 0),
      items,
    });
  }
}

function getTimeInCart(dateAdded?: string): number | undefined {
  if (!dateAdded) return undefined;

  return Math.floor((Date.now() - new Date(dateAdded).getTime()) / 1000);
}

function ProductToItemJSON(item: any) {
  let value: any = item?.cartItem?.priceOption?.unitPrice * item?.cartItem?.priceOption?.quantity;
  value = value.toFixed(2);
  const itemAnalyticsInfo: CartAnalyticsInfo = {
    currency: 'USD',
    value: value,
    items: [
      {
        item_id: `${item?.cartItem?.product?.productId}`,
        item_name: `${item?.cartItem?.product?.title}`,
        item_brand: `${item?.cartItem?.product?.publicationType}`,
        item_variant: `${item?.cartItem?.priceOption?.variantId}`,
        price: item?.cartItem?.priceOption?.unitPrice.toFixed(2),
        quantity: item?.cartItem?.priceOption?.quantity,
      },
    ],
  };
  return itemAnalyticsInfo;
}
function removeCartEvent(item: any) {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  const itemAnalyticsInfo = ProductToItemJSON(item);
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'remove_from_cart',
    ecommerce: itemAnalyticsInfo,
  });
}

function addCartEvent(object: any) {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'add_to_cart',
    ecommerce: object,
  });
}

function checkoutEvent(object: any) {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'begin_checkout',
    ecommerce: object,
  });
}

function purchaseEvent(object: any) {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'purchase',
    ecommerce: object,
  });
}

function viewCartEvent(object: any) {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  window.dataLayer.push({ ecommerce: null });

  window.dataLayer.push({
    event: 'view_cart',
    ecommerce: object,
  });
}

export {
  removeCartEvent,
  addCartEvent,
  purchaseEvent,
  checkoutEvent,
  viewCartEvent,
  getTimeInCart,
  pushUnifiedViewCart,
};
